import {
    Chip,
    FormControl,
    MenuItem,
    Select,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import CustomTooltip from './CustomTooltip';
import { t } from 'i18next';

type HeaderProps = {
    alignLeft?: boolean;
    isAbsMode?: boolean;
    onToggleAbsMode?: () => void;
};

export const SearchTermAnalysisHeader = ({
    alignLeft,
    isAbsMode,
    onToggleAbsMode,
}: HeaderProps): JSX.Element => {
    const theme = useTheme();
    const location = useLocation();

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent={alignLeft ? 'flex-start' : 'flex-end'}
            sx={{ width: '100%' }}
        >
            {location.pathname !== '/brand/visibility/competitors' && (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ borderRadius: 1 }}
                >
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                            value={isAbsMode ? 'abs' : 'top'}
                            onChange={(event) => {
                                const wantAbs = event.target.value === 'abs';

                                if (
                                    typeof isAbsMode === 'boolean' &&
                                    wantAbs !== isAbsMode
                                ) {
                                    onToggleAbsMode?.();
                                }
                            }}
                            displayEmpty
                            renderValue={(selected) => {
                                const isAbsoluteTop = selected === 'abs';

                                return (
                                    <Chip
                                        label={
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing="6px"
                                            >
                                                <span>
                                                    {isAbsoluteTop ? 'Absolute Top' : 'Top'}
                                                </span>

                                                <CustomTooltip
                                                    title={t(
                                                        isAbsoluteTop
                                                            ? 'dashboard.visibility.tooltips.absoluteTop'
                                                            : 'dashboard.visibility.tooltips.top'
                                                    )}
                                                    arrow
                                                    placement="top"
                                                    PopperProps={{
                                                        style: {
                                                            zIndex: 9999,
                                                        },
                                                    }}
                                                >
                                                    <svg style={{cursor: 'help'}} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                        <path d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z" stroke="#637381" stroke-linecap="round" stroke-linejoin="round" />
                                                    </svg>
                                                </CustomTooltip>
                                            </Stack>
                                        }
                                        sx={{
                                            bgcolor:
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(145, 158, 171, 0.20)'
                                                    : 'rgba(145, 158, 171, 0.12)',
                                            color: theme.palette.text.primary,
                                            fontSize: '13px',
                                            height: 24,
                                            borderRadius: '6px',
                                            '&:hover': {
                                                backgroundColor:
                                                    theme.palette.mode === 'dark'
                                                        ? 'rgba(145, 158, 171, 0.20)'
                                                        : 'rgba(145, 158, 171, 0.16)',
                                            },
                                            '& .MuiChip-label': {
                                                px: 1.5,
                                                lineHeight: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                            },
                                        }}
                                    />
                                );
                            }}
                            sx={{
                                borderRadius: '8px',
                                height: 32,
                                fontSize: '13px',
                                width: isAbsMode ? 160 : 100,
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '4px !important',
                                    minHeight: 'unset',
                                    boxSizing: 'border-box',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: `1px solid ${theme.palette.mode === 'dark'
                                        ? 'rgba(145, 158, 171, 0.20)'
                                        : 'rgba(145, 158, 171, 0.32)'
                                        }`,
                                },
                                '& .MuiSelect-icon': {
                                    color: theme.palette.text.primary,
                                },
                            }}
                        >
                            <MenuItem value="top">
                                <Typography
                                    sx={{
                                        color: '#333C47',
                                        fontFamily: 'Public Sans',
                                        fontSize: '14px',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        mr: "6px",
                                        lineHeight: '22px',
                                    }}
                                >
                                    Top
                                </Typography>

                                <CustomTooltip
                                    PopperProps={{
                                        style: {
                                            zIndex: 9999,
                                        },
                                    }}
                                    title={t('dashboard.visibility.tooltips.top')}
                                    arrow
                                    placement="top"
                                >
                                    <svg style={{ cursor: 'help' }} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z" stroke="#637381" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </CustomTooltip>
                            </MenuItem>
                            <MenuItem value="abs">
                                <Typography
                                    sx={{
                                        color: '#333C47',
                                        fontFamily: 'Public Sans',
                                        fontSize: '14px',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        mr: "6px",
                                        lineHeight: '22px',
                                    }}
                                >
                                    Absolute Top
                                </Typography>

                                <CustomTooltip
                                    title={t('dashboard.visibility.tooltips.absoluteTop')}
                                    arrow
                                    placement="top"
                                    PopperProps={{
                                        style: {
                                            zIndex: 9999,
                                        },
                                    }}
                                >
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                        }}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        <svg
                                            style={{ cursor: 'help' }}
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                            fill="none"
                                        >
                                            <path
                                                d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                                                stroke="#637381"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </span>
                                </CustomTooltip>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            )}
        </Stack>
    );
};